import { theme as antdTheme } from 'antd';

/**
 * Global dark theme + layout metrics for the app shell.
 *
 * Target device: 13.4" 2560x1600 at 200% scale => a 1280x800 CSS viewport.
 * Width budget: 1280 - SIDER_WIDTH (176) - content padding (2x16) = ~1072 px of
 * usable content width when the rail is expanded, ~1200 px when collapsed.
 * Height budget: 800 - HEADER_HEIGHT (48) - content padding (2x12) = ~728 px.
 *
 * Every page inherits these numbers; tune density here rather than per page.
 */

/** Palette resolved from the real dark algorithm so no colour is hardcoded. */
const darkTokens = antdTheme.getDesignToken({ algorithm: antdTheme.darkAlgorithm });

/** App header height in CSS px. */
export const HEADER_HEIGHT = 48;

/** Navigation rail width in CSS px (expanded / collapsed). */
export const SIDER_WIDTH = 176;
export const SIDER_COLLAPSED_WIDTH = 48;

/** Padding applied around page content by the shell. */
export const CONTENT_PADDING = '12px 16px';

/** Below this viewport width the rail starts collapsed (first visit only). */
export const SIDER_AUTO_COLLAPSE_WIDTH = 1400;

const appTheme = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    // Type scale: body 13, secondary 12, page title (level 4) 20.
    fontSize: 13,
    fontSizeSM: 12,
    fontSizeLG: 14,
    fontSizeHeading1: 26,
    fontSizeHeading2: 24,
    fontSizeHeading3: 22,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    // Controls: default 32, small 24 (componentSize="small" makes small the default).
    controlHeight: 32,
    borderRadius: 4,
    borderRadiusLG: 6,
    // Keep the flat, low-contrast borders the app already used.
    colorBorder: darkTokens.colorBorderSecondary,
    colorBorderSecondary: darkTokens.colorBorderSecondary,
    colorSplit: darkTokens.colorBorderSecondary,
  },
  components: {
    Layout: {
      headerHeight: HEADER_HEIGHT,
      headerPadding: '0 12px',
      triggerHeight: 36,
    },
    Menu: {
      fontSize: 13,
      itemHeight: 34,
      itemMarginBlock: 2,
      itemMarginInline: 6,
      itemPaddingInline: 8,
      itemBorderRadius: 4,
      iconSize: 14,
      iconMarginInlineEnd: 8,
      collapsedIconSize: 16,
      collapsedWidth: SIDER_COLLAPSED_WIDTH,
    },
    Table: {
      borderColor: darkTokens.colorBorderSecondary,
      headerBorderRadius: 0,
      // One line of content + 8px above/below => ~37px rows.
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 8,
      cellFontSize: 13,
      cellFontSizeSM: 13,
    },
    Card: {
      colorBorderSecondary: darkTokens.colorBorderSecondary,
      bodyPadding: 16,
      bodyPaddingSM: 12,
      headerHeight: 40,
      headerHeightSM: 36,
      headerFontSize: 14,
      headerFontSizeSM: 14,
    },
    Typography: {
      titleMarginTop: '0.6em',
      titleMarginBottom: '0.4em',
    },
    Form: {
      itemMarginBottom: 12,
      verticalLabelPadding: '0 0 4px',
    },
    Descriptions: {
      itemPaddingBottom: 8,
      titleMarginBottom: 8,
    },
    Tabs: {
      horizontalItemPadding: '8px 0',
      horizontalMargin: '0 0 12px 0',
    },
    Collapse: {
      headerPadding: '8px 12px',
      contentPadding: '12px',
    },
    Statistic: {
      titleFontSize: 12,
      contentFontSize: 20,
    },
    Pagination: {
      itemSize: 24,
    },
    Modal: {
      titleFontSize: 16,
    },
    Alert: {
      defaultPadding: '6px 12px',
    },
  },
};

export default appTheme;
